import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  Briefcase, Zap, Eye, Lightbulb, Users, Shield,
} from 'lucide-react';

const reasons = [
  {
    icon: Briefcase,
    title: 'Pendekatan Profesional',
    desc: 'Setiap kasus ditangani dengan standar profesionalisme tertinggi oleh tim hukum kami.',
  },
  {
    icon: Zap,
    title: 'Respons Cepat',
    desc: 'Kami berkomitmen memberikan respons cepat dan tidak membiarkan klien menunggu lama.',
  },
  {
    icon: Eye,
    title: 'Transparansi',
    desc: 'Klien selalu mendapatkan informasi terkini tentang perkembangan kasusnya secara transparan.',
  },
  {
    icon: Lightbulb,
    title: 'Pendampingan Strategis',
    desc: 'Strategi hukum yang terencana dan berbasis riset mendalam untuk hasil terbaik.',
  },
  {
    icon: Users,
    title: 'Tim Berpengalaman',
    desc: 'Didukung tim advokat berpengalaman dengan rekam jejak yang terbukti dalam berbagai bidang.',
  },
  {
    icon: Shield,
    title: 'Kerahasiaan Klien',
    desc: 'Setiap informasi klien dijaga kerahasiaannya dengan protokol keamanan yang ketat.',
  },
];

const cardVariants = {
  hidden:  { opacity: 0, scale: 0.9 },
  visible: (i) => ({
    opacity: 1, scale: 1,
    transition: { duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function WhyUs() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <section id="mengapa" className="relative py-24 lg:py-32 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-600/30 to-transparent" />

      {/* Background pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4950e' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      {/* Large accent circle */}
      <div
        className="absolute -right-40 top-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full opacity-5"
        style={{ background: 'radial-gradient(circle, #d4950e 0%, transparent 70%)' }}
      />

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="inline-block text-gold-400 text-sm font-semibold tracking-widest uppercase mb-3">
            Keunggulan Kami
          </span>
          <h2 className="section-title mb-4">
            Mengapa Memilih{' '}
            <span className="gold-text">Kami?</span>
          </h2>
          <div className="divider-gold mx-auto mb-5" />
          <p className="section-subtitle max-w-2xl mx-auto">
            Kepercayaan klien adalah prioritas utama kami. Berikut alasan mengapa
            ratusan klien mempercayakan permasalahan hukum mereka kepada kami.
          </p>
        </motion.div>

        {/* Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {reasons.map((item, i) => (
            <motion.div
              key={item.title}
              custom={i}
              variants={cardVariants}
              initial="hidden"
              animate={inView ? 'visible' : 'hidden'}
              whileHover={{ y: -6, transition: { duration: 0.25 } }}
              className="group relative glass rounded-2xl p-8 border border-white/5 hover:border-gold-600/25 transition-all duration-400 overflow-hidden"
            >
              {/* Hover shimmer */}
              <div className="absolute inset-0 bg-gradient-to-br from-gold-500/0 to-gold-500/0 group-hover:from-gold-500/5 group-hover:to-transparent transition-all duration-500 rounded-2xl" />

              {/* Number badge */}
              <span className="absolute top-5 right-5 font-serif text-5xl font-bold text-white/[0.04] group-hover:text-white/[0.07] transition-all duration-300 select-none">
                {String(i + 1).padStart(2, '0')}
              </span>

              <div className="relative z-10">
                <div className="w-12 h-12 rounded-xl bg-gold-600/15 group-hover:bg-gold-600/25 flex items-center justify-center mb-5 transition-all duration-300 border border-gold-600/20">
                  <item.icon size={22} className="text-gold-400" strokeWidth={1.8} />
                </div>

                <h3 className="font-serif text-xl font-bold text-white mb-3">
                  {item.title}
                </h3>
                <p className="text-navy-300 text-sm leading-relaxed">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
